import { DownOutlined } from '@ant-design/icons';
import { Bar } from '@ant-design/plots';
import { useLazyQuery } from '@apollo/client';
import {
  Challenge,
  DashboardChallengeIdeaStats,
  GET_DASHBOARD_CHALLENGE_IDEA_BUSINESS_AREAS,
} from '@frontend/data-access';
import { Button, Card, Dropdown } from 'antd';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useState } from 'react';

/* eslint-disable-next-line */
export interface ChallengeDashboardBusinessAreaProps {
  challenge: Challenge & { __typename?: string };
}

export function ChallengeDashboardBusinessArea({
  challenge,
}: ChallengeDashboardBusinessAreaProps) {
  const { t } = useTranslation('common');

  const sortOptions = [
    {
      key: 'HL',
      label: t('highest_to_lowest'),
    },
    {
      key: 'LH',
      label: t('lowest_to_highest'),
    },
  ];

  const [sortOption, setSortOption] = useState(sortOptions[0]);
  const [data, setData] = useState<DashboardChallengeIdeaStats[]>([]);
  const [getData, { data: rawData, loading }] = useLazyQuery<{
    dashboardChallengeIdeasByAreas: DashboardChallengeIdeaStats[];
  }>(GET_DASHBOARD_CHALLENGE_IDEA_BUSINESS_AREAS, {
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    getData({
      variables: {
        challengeId: challenge.id,
      },
    });
  }, [challenge.id, getData]);

  useEffect(() => {
    if (!rawData?.dashboardChallengeIdeasByAreas) return;
    // sort
    const sortedData = [...rawData.dashboardChallengeIdeasByAreas].sort(
      (a, b) => {
        if (sortOption.key === 'HL') {
          return b.count - a.count;
        }
        return a.count - b.count;
      }
    );
    if (sortedData.length == 0) {
      setData([{ name: t('no_data'), count: 0 }]);
    } else {
      setData(sortedData);
    }
  }, [rawData, sortOption.key, t]);

  const config = {
    data: data,
    xField: 'name',
    yField: 'count',
    color: '#529FF0',
    yAxis: {
      label: {
        style: {
          fontFamily: 'Lufga',
          fill: 'rgba(0, 0, 0, 0.65)',
          textAlign: 'right',
        },
      },
    },
  };

  return (
    <Card
      style={{ borderRadius: 20 }}
      data-test="challenge-dashboard-business-area"
    >
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-900 font-medium">
            {t('business_functional_areas_covered')}
          </div>
          <div>
            <Dropdown
              menu={{
                items: sortOptions.map((item) => ({
                  ...item,
                  onClick: () => setSortOption(item),
                })),
              }}
              placement="bottomRight"
              trigger={['click']}
            >
              <Button shape="round">
                {sortOption.label}
                <DownOutlined />
              </Button>
            </Dropdown>
          </div>
        </div>
        <Bar loading={loading} {...config} />
      </div>
    </Card>
  );
}

export default ChallengeDashboardBusinessArea;
